import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectMultiScreen } from '@store/actionSlices/multiScreen'
import { ProjectIdentity } from '@store/types'

import { generateId } from '@utilities/helper'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

interface FirebaseRefreshTriggerParams {
  state: 'registered' | 'cleared'
  sessionKey?: string
  key?: string
}

interface ClearCacheProps {
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
}

const useClearCache = ({
  projectIdentity,
  session,
}: ClearCacheProps): [boolean, () => void] => {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const multiScreen = useSelector(selectMultiScreen)

  const [refreshTriggerKeyRestTimerId, setRefreshTriggerKeyRestTimerId] =
    useState<ReturnType<typeof setTimeout>>()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const setRefreshTriggerKeyOnFirebase = async ({
    state,
    sessionKey,
    key = '',
  }: FirebaseRefreshTriggerParams) => {
    await firebaseControlQuery.update(
      {
        [`clearCacheTriggerKey`]: key,
        [`clearCacheKeyState`]: state,
      },
      sessionKey
    )
  }

  const handleRefreshClick = async () => {
    setIsRefreshing(true)

    const triggerKey = generateId(10)
    const isMultiScreenActive = multiScreen.active

    if (isMultiScreenActive) {
      const activeProjectSessionKeys = multiScreen.displayScreens.flatMap(
        (screen) =>
          screen.sessionKeys
            .filter((sess) => sess.projectId === projectIdentity.projectName)
            .map((sess) => sess.key)
      )

      await Promise.allSettled(
        activeProjectSessionKeys.map((sessionKey) =>
          setRefreshTriggerKeyOnFirebase({
            state: 'registered',
            sessionKey,
            key: triggerKey,
          })
        )
      )
    }

    await setRefreshTriggerKeyOnFirebase({
      state: 'registered',
      key: triggerKey,
    })
  }

  const resetRefreshTriggerKeyTimer = () => {
    setIsRefreshing(false)
    if (refreshTriggerKeyRestTimerId) {
      clearTimeout(refreshTriggerKeyRestTimerId)
      setRefreshTriggerKeyRestTimerId(undefined)
    }
  }

  const resetRefreshKeyStateAndReload = async (state?: string) => {
    if (state !== 'cleared') return
    await firebaseControlQuery.update({
      [`clearCacheKeyState`]: '',
    })
    window.location.reload()
  }

  const handleRefreshTriggerKeyChange = async (
    refreshTriggerKey?: string,
    myRefreshKeyState?: string
  ) => {
    if (!refreshTriggerKey) {
      resetRefreshTriggerKeyTimer()
      resetRefreshKeyStateAndReload(myRefreshKeyState)
      return
    }

    if (!refreshTriggerKeyRestTimerId) {
      setRefreshTriggerKeyRestTimerId(
        setTimeout(() => {
          setRefreshTriggerKeyOnFirebase({
            state: 'cleared',
            key: '',
          })
        }, 30 * 1000)
      )
    }
  }

  useEffect(() => {
    if (!session) {
      return
    }

    const {
      clearCacheTriggerKey: firebaseRefreshTriggerKey,
      clearCacheKeyState: firebaseRefreshKeyState,
    } = session
    handleRefreshTriggerKeyChange(
      firebaseRefreshTriggerKey,
      firebaseRefreshKeyState
    )
  }, [session])

  return [isRefreshing, handleRefreshClick]
}

export default useClearCache
